/**
 * <PersonRow> — une personne en ligne d'une carte-liste, qui ouvre SA conversation (molecule).
 *
 * Son visage, son nom, ce que la ligne dit d'elle (`caption` : les matières d'un tuteur, la classe
 * d'un élève), le nombre de messages non lus, et le chevron. Toute la ligne presse.
 *
 * Sans `onPress`, la ligne ne presse pas et ne montre pas de chevron : elle présente la personne,
 * rien de plus.
 *
 * `selected` : sa conversation est ouverte à côté (deux volets). Le visage prend l'anneau de
 * sélection du livre, la ligne le fond des lignes mises en avant (`primary-soft`), et la pastille
 * des non-lus se retire : une conversation ouverte est lue.
 *
 * `trailing` : ce que la ligne porte au bout (une chip, une case) — avant le chevron s'il y en a un.
 * `dimmed` : la personne n'est pas là (un élève absent du live) ; son visage s'estompe.
 *
 * Elle ne dessine pas de carte : la liste est UNE carte-liste. Lue par `<TutorRow>` (un tuteur vu par
 * l'élève, sa capsule).
 *
 * Composes DS pieces only — `<CardListRow>`, `<Avatar>`, `<Text>`, `<Badge>`, `<Icon>`.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { cappedCount } from "../../utils/cappedCount"
import { CardListRow } from "../CardList"
import { BOW_ROOM } from "../SessionRow/SessionRow"

export type PersonRowProps = {
    name: string
    avatarUrl?: string | null
    /** `tutor` : la capsule d'un tuteur (liseré blanc, nœud jaune). Défaut `default` : le rond simple. */
    avatarVariant?: "default" | "tutor"
    /** Sous le nom. Absent, le nom est seul. */
    caption?: string | null
    /** Messages non lus — au-delà de 9, la pastille dit « 9+ ». Défaut 0, rien. */
    unreadCount?: number
    /** Toute la ligne presse. Absent → elle ne presse pas, et n'a pas de chevron. */
    onPress?: () => void
    /** Sa conversation est ouverte à côté. Défaut `false`. */
    selected?: boolean
    /** Un filet au-dessus de la ligne. Défaut `false`. */
    divided?: boolean
    /** Au bout de la ligne, avant le chevron. Absent → rien. */
    trailing?: React.ReactNode
    /** La personne n'est pas là : son visage s'estompe. Défaut `false`. */
    dimmed?: boolean
    testID?: string
}

/** Le visage d'une ligne de personne, la taille de liste du livre. */
export const PERSON_AVATAR_SIZE = 44

export function PersonRow(props: PersonRowProps) {
    const {
        name,
        avatarUrl,
        avatarVariant = "default",
        caption,
        unreadCount = 0,
        onPress,
        selected = false,
        divided = false,
        trailing,
        dimmed = false,
        testID,
    } = props

    const unread = selected ? 0 : unreadCount
    const hasCaption = caption != null && caption !== ""

    const line = (
        <CardListRow divided={divided}>
            <Row gap="3" align="center" style={avatarVariant === "tutor" ? styles.bowLine : undefined}>
                <Avatar
                    uri={avatarUrl ?? undefined}
                    name={name}
                    size={PERSON_AVATAR_SIZE}
                    variant={avatarVariant}
                    selected={selected}
                    dimmed={dimmed}
                />
                <Stack gap="0" style={styles.body}>
                    <Text size="text-md" weight="bold" numberOfLines={1}>
                        {name}
                    </Text>
                    {hasCaption && (
                        <Text size="text-sm" color="text-muted" numberOfLines={1}>
                            {caption}
                        </Text>
                    )}
                </Stack>
                {/* La chip se cale en haut de sa boîte : dans la sienne, elle reste au milieu de la ligne. */}
                {unread > 0 && (
                    <View>
                        <Badge text={cappedCount(unread)} variant="danger" />
                    </View>
                )}
                {trailing}
                {onPress != null && <Icon name="chevron-right" size={16} color="text-muted" />}
            </Row>
        </CardListRow>
    )

    return (
        <View testID={testID} style={selected ? styles.selected : undefined}>
            {onPress == null ? (
                line
            ) : (
                <Pressable
                    onPress={onPress}
                    accessibilityRole="button"
                    accessibilityLabel={accessibilityLabel(name, caption ?? undefined, unread)}
                    accessibilityState={{ selected }}
                >
                    {line}
                </Pressable>
            )}
        </View>
    )
}

function accessibilityLabel(name: string, caption: string | undefined, unreadCount: number): string {
    const unread = unreadCount > 0 ? `${unreadCount} non ${unreadCount > 1 ? "lus" : "lu"}` : null
    return [name, caption, unread].filter((part) => part != null && part !== "").join(", ")
}

const styles = StyleSheet.create((theme) => ({
    /** La place du nœud, qui pend sous la capsule d'un tuteur. */
    bowLine: { paddingBottom: BOW_ROOM },
    body: { flex: 1, flexShrink: 1, minWidth: 0 },
    selected: { backgroundColor: theme.colors["primary-soft"] },
}))
